import { Tag, Award, Star } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';

interface ProductDetailsProps {
  category: string;
  tags: string[];
  isFeatured: boolean;
  isNew: boolean;
  details: string[];
  rating?: number;
}

export function ProductDetails({
  category,
  tags,
  isFeatured,
  isNew,
  details,
  rating,
}: ProductDetailsProps) {
  const { language } = useLanguage();
  const t = useTranslation(language);
  
  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      {/* Category and Tags */}
      <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
        <div className="flex items-center gap-1.5" style={{ fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
          <Tag className="w-3.5 h-3.5" strokeWidth={2.75} />
          <span className="capitalize">{category}</span>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="tag tag-neutral">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2">
        {isNew && (
          <span className="tag tag-accent flex items-center gap-1">
            <Award className="w-3 h-3" strokeWidth={2.75} />
            {t('product.newArrival')}
          </span>
        )}
        {isFeatured && (
          <span className="tag tag-accent-2 flex items-center gap-1">
            <Star className="w-3 h-3" strokeWidth={2.75} />
            {t('product.featured')}
          </span>
        )}
        {rating !== undefined && rating >= 4.5 && (
          <span className="tag tag-outline flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" strokeWidth={2.75} />
            {t('product.topRated')}
          </span>
        )}
      </div>

      {/* Product Details */}
      {details.length > 0 && (
        <div>
          <p style={{ fontFamily: 'var(--font-heading)', fontSize: 17, margin: '0 0 var(--space-2)' }}>{t('product.productDetails')}</p>
          <ul className="flex flex-col" style={{ gap: 'var(--space-2)', margin: 0, padding: 0, listStyle: 'none' }}>
            {details.map((detail, index) => (
              <li key={index} className="flex items-start" style={{ color: 'color-mix(in srgb, var(--color-text) 82%, transparent)' }}>
                <span style={{ marginRight: 8, color: 'var(--color-accent)' }}>•</span>
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
